/**
 * export.ts — Session Download
 *
 * Exports a result pill as a ZIP file containing:
 *   - results.csv  (session header + timestamped timeline)
 *   - recording.mp4 (video blob, or video-url.txt fallback on CORS/fetch failure)
 *
 * Three pill paths:
 *   stream-*   → webcam or file stream. Video at pill.recordingUrl.
 *   offline-*  → offline batch job. Video at pill.videoUrl (already resolved).
 *   <an_|UUID> → uploaded/history pill. Video via getAnnotatedVideoUrl(pill.id)
 *                (public Analysis.artifacts for an_ ids; legacy internal
 *                 tokened URL for pre-cutover UUID pills).
 */

import JSZip from 'jszip';
// PRI-496 Lane B: annotated playback rides public Analysis.artifacts for
// an_ ids (signed URL, token unnecessary); legacy UUID pills keep the
// internal token-query URL until those history rows age out.
import { getAnnotatedVideoUrl, isPublicAnalysisId } from '../core/lib/api/public-inference';
import type { NarrativeEntry } from './narrative';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Duck-typed subset of ResultPill sufficient for export. */
export interface ExportablePill {
  id: string;
  prompt: string;
  answer?: 'YES' | 'NO';
  confidence: number;           // stored as 0-100
  query_type?: string;
  liveData?: { query_type?: string; search_term?: string };
  resultPayload?: Record<string, unknown> | null;
  narrative: NarrativeEntry[];
  streamHistory?: Array<{
    frame_num: number;
    answer: string;
    confidence: number;
    elapsed_s?: number;
    term_confidences?: Record<string, number>;
    recording_elapsed_ms?: number;
  }>;
  videoUrl?: string | null;
  recordingUrl?: string;
}

interface ExportRow {
  timestamp_s: number;
  event_type: 'summary' | 'narrative' | 'clip' | 'frame';
  answer: string;
  confidence: string;
  text: string;
  details: string;
}

interface ExportData {
  header: Record<string, string>;
  rows: ExportRow[];
  videoUrl: string | null;
  recordingUrl: string | null;
}

// ── buildPillExport ───────────────────────────────────────────────────────────

/**
 * Extract all export data from a pill.
 * Pure function — no side effects, no network calls.
 */
export function buildPillExport(pill: ExportablePill): ExportData {
  const isStreamPill = pill.id.startsWith('stream-');
  const pillType = isStreamPill ? 'stream' : 'upload';
  const queryType = pill.query_type ?? pill.liveData?.query_type ?? 'unknown';
  const confidence = pill.confidence / 100; // convert 0-100 → 0.00-1.00

  const header: Record<string, string> = {
    session_exported_at: new Date().toISOString(),
    prompt:              pill.prompt,
    answer:              pill.answer ?? '',
    confidence:          confidence.toFixed(4),
    query_type:          queryType,
    pill_type:           pillType,
  };

  const rows: ExportRow[] = [];

  // ── Summary row ──────────────────────────────────────────────────────────
  rows.push({
    timestamp_s: 0,
    event_type:  'summary',
    answer:      pill.answer ?? '',
    confidence:  confidence.toFixed(4),
    text:        '',
    details:     JSON.stringify({
      search_term: pill.liveData?.search_term ?? queryType,
      frame_count: (pill.streamHistory ?? []).length,
    }),
  });

  // ── Narrative rows ───────────────────────────────────────────────────────
  for (const entry of pill.narrative) {
    const ts = entry.recording_elapsed_ms != null
      ? entry.recording_elapsed_ms / 1000
      : entry.elapsed_s;
    rows.push({
      timestamp_s: ts,
      event_type:  'narrative',
      answer:      '',
      confidence:  '',
      text:        entry.text,
      details:     JSON.stringify({ type: entry.type, wall_clock: entry.wall_clock }),
    });
  }

  // ── Stream frame rows (stream pills only) ────────────────────────────────
  if (isStreamPill && pill.streamHistory && pill.streamHistory.length > 0) {
    for (const h of pill.streamHistory) {
      const ts = h.recording_elapsed_ms != null
        ? h.recording_elapsed_ms / 1000
        : (h.elapsed_s ?? 0);
      rows.push({
        timestamp_s: ts,
        event_type:  'frame',
        answer:      h.answer,
        confidence:  h.confidence.toFixed(4),
        text:        '',
        details:     JSON.stringify({
          frame_num:         h.frame_num,
          term_confidences:  h.term_confidences ?? {},
        }),
      });
    }
  }

  // ── Clip rows (upload/offline pills with resultPayload.clips) ────────────
  if (!isStreamPill && pill.resultPayload) {
    const rawClips = (pill.resultPayload as Record<string, unknown>).clips;
    if (Array.isArray(rawClips)) {
      for (const clip of rawClips as Array<{ start_time: number; end_time: number; confidence?: number | null; confidences?: Record<string, number> }>) {
        rows.push({
          timestamp_s: clip.start_time,
          event_type:  'clip',
          answer:      '',
          confidence:  clip.confidence != null ? clip.confidence.toFixed(4) : '',
          text:        '',
          details:     JSON.stringify({ start_time: clip.start_time, end_time: clip.end_time }),
        });
      }
    }
  }

  // Sort all rows ascending by timestamp_s
  rows.sort((a, b) => a.timestamp_s - b.timestamp_s);

  return {
    header,
    rows,
    videoUrl:     pill.videoUrl ?? null,
    recordingUrl: pill.recordingUrl ?? null,
  };
}

// ── exportRowsToCsv ───────────────────────────────────────────────────────────

/** Escape a CSV field value (quote if contains comma, quote, or newline). */
function csvField(value: string | number): string {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * Render header + timeline rows as a two-section CSV string.
 *
 * Section 1 — Session metadata (Field, Value pairs)
 * Section 2 — Timestamped timeline rows
 *
 * The two sections are separated by a blank line.
 */
export function exportRowsToCsv(
  header: Record<string, string>,
  rows: ExportRow[],
): string {
  const lines: string[] = [];

  // Section 1: header
  lines.push('Field,Value');
  for (const [key, value] of Object.entries(header)) {
    lines.push(`${csvField(key)},${csvField(value)}`);
  }

  // Blank line between sections
  lines.push('');

  // Section 2: timeline
  lines.push('timestamp_s,event_type,answer,confidence,text,details');
  for (const row of rows) {
    lines.push([
      csvField(row.timestamp_s),
      csvField(row.event_type),
      csvField(row.answer),
      csvField(row.confidence),
      csvField(row.text),
      csvField(row.details),
    ].join(','));
  }

  return lines.join('\n');
}

// ── downloadVideoAsBlob ───────────────────────────────────────────────────────

/**
 * Fetch a session video as a Blob. Requires a fresh auth token.
 *
 * Handles all three pill paths:
 *   stream-*  → fetches pill.recordingUrl with &token=...
 *   offline-* → fetches pill.videoUrl directly (already resolved)
 *   <an_|UUID> → fetches getAnnotatedVideoUrl(pill.id) — public artifacts URL
 *               for an_ ids, legacy internal tokened URL for UUID pills
 *
 * Returns null on any failure (CORS, 4xx, network). The caller provides a
 * video-url.txt fallback in the ZIP when this returns null.
 */
export async function downloadVideoAsBlob(
  pill: ExportablePill,
  token: string | null,
): Promise<Blob | null> {
  try {
    let url: string;
    const isStream  = pill.id.startsWith('stream-');
    const isOffline = pill.id.startsWith('offline-');

    if (isStream && pill.recordingUrl) {
      // Stream pill: append token to the recording endpoint
      url = token
        ? `${pill.recordingUrl}&token=${encodeURIComponent(token)}`
        : pill.recordingUrl;
    } else if (isOffline && pill.videoUrl) {
      // Offline pill: pill.id is "offline-{timestamp}" not the job id. The
      // attached videoUrl is already playable (signed artifacts URL, or the
      // legacy tokened internal URL) — fetch it directly.
      url = pill.videoUrl;
    } else if (!isStream && !isOffline) {
      // Upload/history pill: pill.id IS the analysis/job id.
      const base = await getAnnotatedVideoUrl(pill.id);
      if (!base) return null;
      url = isPublicAnalysisId(pill.id)
        ? base // signed artifacts URL
        : (token ? `${base}?token=${encodeURIComponent(token)}` : base);
    } else {
      return null; // stream with no recordingUrl, or offline with no videoUrl
    }

    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

// ── triggerZipDownload ────────────────────────────────────────────────────────

/**
 * Build ZIP and trigger browser download.
 * Orchestrates: CSV generation → video fetch → ZIP assembly → download.
 */
export async function triggerZipDownload(
  pill: ExportablePill,
  token: string | null,
): Promise<void> {
  const { header, rows, videoUrl, recordingUrl } = buildPillExport(pill);
  const csv = exportRowsToCsv(header, rows);

  const videoBlob = await downloadVideoAsBlob(pill, token);

  // Build slug: lowercase, spaces→hyphens, strip non-alphanumeric, max 40 chars
  const slug = pill.prompt
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 40)
    .replace(/-+$/, '');
  const date = new Date().toISOString().slice(0, 10);
  const basename = `primate-${slug}-${date}`;

  const zip = new JSZip();
  zip.file('results.csv', csv);

  if (videoBlob) {
    // Determine extension from blob type (mp4 or webm)
    const ext = videoBlob.type.includes('mp4') || videoBlob.type.includes('mp4')
      ? 'mp4' : videoBlob.type.includes('webm') ? 'webm' : 'mp4';
    zip.file(`recording.${ext}`, videoBlob);
  } else {
    // CORS/fetch failure fallback: include direct URL for the user to download manually
    const fallbackUrl = recordingUrl ?? videoUrl ?? '';
    zip.file('video-url.txt', `Video URL (download separately if video is missing):\n${fallbackUrl}\n`);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });

  // Trigger browser download via synthetic anchor click
  const a = document.createElement('a');
  a.href = URL.createObjectURL(zipBlob);
  a.download = `${basename}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}
